'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { getTerraGraphCategoryColors, token } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import {
  formatAxisLabel,
  formatTooltipValue,
  type LineTooltipParams,
} from './format';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface LineChartProps {
  series: {
    name: string;
    data: number[];
    strokeDashArray?: number;
  }[];
  categories: string[];
  title?: string;
  height?: number | string;
  width?: number | string;
  colors?: string[];
  xaxisTitle?: string;
  yaxisTitle?: string;
  yaxisMin?: number;
  yaxisMax?: number;
  showDataLabels?: boolean;
  dataLabelsFormatter?: (value: number) => string;
  tooltipCustom?: (options: LineTooltipParams) => string;
  tooltipFormatter?: (value: number) => string;
  showLegend?: boolean;
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
  curve?: 'smooth' | 'straight' | 'stepline';
  showXAxisTooltip?: boolean;
  strokeDashArray?: number;
  showXAxisGridLines?: boolean;
  hideOverlappingXAxisLabels?: boolean;
  rotateXAxisLabels?: number;
  showLegendForSingleSeries?: boolean;
  axisTitleStyle?: {
    color?: string;
    fontSize?: string;
    fontFamily?: string;
    fontWeight?: string;
    cssClass?: string;
  };
  markers?: {
    size?: number;
    strokeWidth?: number;
    offsetX?: number;
  };
  xaxisType?: 'category' | 'datetime' | 'numeric';
  legendOffsetY?: number;
  allowMouseWheelZoom?: boolean;
  legendItemMargin?: {
    horizontal?: number;
    vertical?: number;
  };
}

export function LineChart({
  series,
  categories,
  title = '',
  height = 350,
  width = '100%',
  colors = getTerraGraphCategoryColors(),
  xaxisTitle = '',
  yaxisTitle = '',
  yaxisMin,
  yaxisMax,
  showDataLabels = false,
  dataLabelsFormatter,
  tooltipCustom,
  tooltipFormatter,
  showLegend = true,
  legendPosition = 'bottom',
  curve = 'smooth',
  showXAxisTooltip = true,
  strokeDashArray = 4,
  showXAxisGridLines = true,
  hideOverlappingXAxisLabels = true,
  rotateXAxisLabels = -45,
  showLegendForSingleSeries = false,
  axisTitleStyle,
  markers,
  xaxisType = 'category',
  legendOffsetY,
  allowMouseWheelZoom = true,
  legendItemMargin,
}: LineChartProps) {
  const { mode: themeMode } = useThemeMode();

  const options = useMemo<ApexOptions>(() => {
    if (!series?.length || !categories?.length) {
      return {
        chart: {
          type: 'line',
          toolbar: {
            show: false,
          },
        },
      };
    }

    return {
      chart: {
        type: 'line',
        toolbar: {
          show: false,
        },
        foreColor: 'currentColor',
        animations: {
          enabled: true,
          speed: 300,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        zoom: {
          allowMouseWheelZoom,
        },
      },
      stroke: {
        curve,
        width: 2,
        dashArray: series.map((s) => s.strokeDashArray ?? 0),
      },
      colors,
      dataLabels: {
        enabled: showDataLabels,
        ...(dataLabelsFormatter && { formatter: dataLabelsFormatter }),
        style: {
          fontSize: '14px',
          fontFamily: 'inherit',
          fontWeight: 'normal',
        },
        background: {
          enabled: true,
          padding: 4,
          borderRadius: 4,
        },
      },
      title: {
        text: title,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'normal',
        },
      },
      xaxis: {
        type: xaxisType,
        categories,
        tooltip: {
          enabled: showXAxisTooltip,
        },
        title: {
          text: xaxisTitle,
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            ...(axisTitleStyle || {}),
          },
        },
        labels: {
          hideOverlappingLabels: hideOverlappingXAxisLabels,
          rotate: rotateXAxisLabels,
          style: {
            fontSize: '12px',
          },
        },
        axisTicks: {
          show: showXAxisGridLines,
        },
      },
      yaxis: {
        ...(yaxisMin !== undefined && { min: yaxisMin }),
        ...(yaxisMax !== undefined && { max: yaxisMax }),
        title: {
          text: yaxisTitle,
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            ...(axisTitleStyle || {}),
          },
        },
        labels: {
          style: {
            fontSize: '12px',
          },
          formatter: formatAxisLabel,
        },
      },
      tooltip: {
        enabled: true,
        ...(tooltipCustom
          ? { custom: tooltipCustom }
          : { theme: themeMode === 'dark' ? 'dark' : 'light' }),
        y: {
          formatter: tooltipFormatter ?? formatTooltipValue,
        },
        style: {
          fontSize: '12px',
        },
      },
      legend: {
        show: showLegend,
        showForSingleSeries: showLegendForSingleSeries,
        position: legendPosition,
        fontSize: '14px',
        fontFamily: 'inherit',
        labels: {
          useSeriesColors: false,
        },
        markers: markers || {},
        ...(legendOffsetY !== undefined && { offsetY: legendOffsetY }),
        ...(legendItemMargin !== undefined && { itemMargin: legendItemMargin }),
      },
      grid: {
        borderColor: token('colors.border.default'),
        strokeDashArray,
        xaxis: {
          lines: {
            show: showXAxisGridLines,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: 'bottom',
              showForSingleSeries: showLegendForSingleSeries,
            },
          },
        },
      ],
    };
  }, [
    series,
    categories,
    colors,
    showDataLabels,
    dataLabelsFormatter,
    title,
    xaxisTitle,
    yaxisTitle,
    yaxisMin,
    yaxisMax,
    tooltipCustom,
    tooltipFormatter,
    showLegend,
    legendPosition,
    curve,
    themeMode,
    showXAxisTooltip,
    strokeDashArray,
    showXAxisGridLines,
    hideOverlappingXAxisLabels,
    rotateXAxisLabels,
    showLegendForSingleSeries,
    markers,
    axisTitleStyle,
    xaxisType,
    legendOffsetY,
    allowMouseWheelZoom,
    legendItemMargin,
  ]);

  if (!options) {
    return null;
  }

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="line"
      height={height}
      width={width}
    />
  );
}

export default LineChart;
